import { gql } from '@apollo/client'
import { useApolloClient, useMutation } from '@apollo/client/react'
import BookOnlineIcon from '@mui/icons-material/BookOnline'
import BugReportIcon from '@mui/icons-material/BugReport'
import BusinessIcon from '@mui/icons-material/Business'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DashboardIcon from '@mui/icons-material/Dashboard'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import PersonIcon from '@mui/icons-material/Person'
import PlaceIcon from '@mui/icons-material/Place'
import SettingsIcon from '@mui/icons-material/Settings'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import {
  AppBar,
  Avatar,
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Toolbar,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { getAssetUrl, getImageUrl, isFeatureEnabled } from './config'
import { SiteRole, UserLogoutDocument } from './gql/graphql'
import { useCurrentUser, useSiteContext } from './hooks'

gql`
  mutation UserLogout {
    logout {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

const drawerWidth = 180
const miniDrawerWidth = 64

interface MenuItemDef {
  text: string
  icon?: React.ReactNode
  path: string
  superAdminOnly?: boolean
  devOnly?: boolean
}

interface MenuGroupDef {
  group: string
  icon: React.ReactNode
  children: MenuItemDef[]
  featureGate?: string
}

type NavItem = MenuItemDef | MenuGroupDef

function isGroup(item: NavItem): item is MenuGroupDef {
  return 'group' in item
}

const navItems: NavItem[] = [
  { text: '数据看板', icon: <DashboardIcon />, path: '/dashboard' },
  { text: '场地管理', icon: <BookOnlineIcon />, path: '/slots' },
  { text: '订单管理', icon: <ShoppingCartIcon />, path: '/orders' },
  {
    group: '优惠券',
    icon: <CardGiftcardIcon />,
    featureGate: 'coupon',
    children: [{ text: '模板管理', path: '/coupon-templates' }],
  },
  {
    group: '场馆管理',
    icon: <BusinessIcon />,
    children: [
      { text: '时段模板', path: '/slot-templates' },
      { text: '人员设置', path: '/staff', superAdminOnly: true },
      { text: '场馆设置', path: '/sites' },
    ],
  },
  {
    text: 'Test',
    icon: <BugReportIcon />,
    path: '/test',
    superAdminOnly: true,
    devOnly: true,
  },
]

export default function MainWindow() {
  const [currentUser] = useCurrentUser()
  const { sites, currentSite, switchSite } = useSiteContext()
  const [drawerOpen, setDrawerOpen] = useState(true)
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({})
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)
  const userMenuOpen = Boolean(userMenuAnchor)
  const navigate = useNavigate()
  const location = useLocation()
  const [logoutMutation] = useMutation(UserLogoutDocument)
  const apolloClient = useApolloClient()

  // When user has no sites, only site management menu should be enabled
  const hasSites = sites.length > 0

  const isDevParam = new URLSearchParams(location.search).has('dev')

  // Filter nav items, applying superAdminOnly and devOnly rules to leaf items
  const visibleNavItems = useMemo(() => {
    const filterItem = (item: MenuItemDef) => {
      if (item.superAdminOnly && currentSite?.role !== SiteRole.SuperAdmin) return false
      if (item.devOnly && !isDevParam) return false
      return true
    }
    return navItems
      .map((item) => {
        if (isGroup(item)) {
          if (item.featureGate && !isFeatureEnabled(item.featureGate)) return null
          const children = item.children.filter(filterItem)
          return children.length > 0 ? { ...item, children } : null
        }
        return filterItem(item) ? item : null
      })
      .filter(Boolean) as NavItem[]
  }, [currentSite?.role, isDevParam])

  // Redirect to site management page when user has no sites
  useEffect(() => {
    if (!hasSites && location.pathname !== '/sites') {
      navigate('/sites', { replace: true })
    }
  }, [hasSites, location.pathname, navigate])

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen)
  }

  const handleMenuItemClick = (path?: string, event?: React.MouseEvent) => {
    // Blur the button to prevent focus conflicts with dialogs
    if (event?.currentTarget) {
      ;(event.currentTarget as HTMLElement).blur()
    }

    if (path) {
      navigate(path)
    }
  }

  const handleLogout = async () => {
    setUserMenuAnchor(null) // Close menu

    try {
      const { data } = await logoutMutation()

      if (data?.logout.__typename === 'GraphqlRequestError') {
        console.error('Logout failed:', data.logout.message)
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Always clear local state regardless of mutation result
      localStorage.removeItem('authToken')
      await apolloClient.clearStore()
      // Force page reload to trigger LoginRedirector re-authentication check
      window.location.href = '/'
    }
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo/Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={getAssetUrl('/logo.svg')} alt="Logo" style={{ height: 32 }} />
            <Typography variant="h6" component="div">
              几点几
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />

          {/* Right side - Site selector, Notifications and User */}
          {currentSite && (
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <PlaceIcon sx={{ fontSize: 20, mr: 0.5, color: 'text.secondary' }} />
              {sites.length > 1 ? (
                <Select
                  value={currentSite.id}
                  onChange={(e: SelectChangeEvent) => switchSite(e.target.value)}
                  size="small"
                  variant="standard"
                  disableUnderline
                  data-testid="site-selector"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {sites.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Typography variant="body2" data-testid="site-name-display">
                  {currentSite.name}
                </Typography>
              )}
            </Box>
          )}

          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            aria-controls={userMenuOpen ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={userMenuOpen ? 'true' : undefined}
          >
            <Avatar
              sx={{ width: 32, height: 32 }}
              src={currentUser?.avatar ? getImageUrl(currentUser.avatar) : undefined}
            >
              {!currentUser?.avatar && <PersonIcon />}
            </Avatar>
            <Typography variant="body2">{currentUser?.name}</Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        id="user-menu"
        anchorEl={userMenuAnchor}
        open={userMenuOpen}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setUserMenuAnchor(null)
            navigate('/settings')
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>个人设置</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>退出登录</ListItemText>
        </MenuItem>
      </Menu>

      <Drawer
        variant="permanent"
        open={drawerOpen}
        sx={{
          width: drawerOpen ? drawerWidth : miniDrawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          '& .MuiDrawer-paper': {
            width: drawerOpen ? drawerWidth : miniDrawerWidth,
            boxSizing: 'border-box',
            mt: 8,
            height: 'calc(100% - 64px)',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          },
        }}
      >
        <List sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          {visibleNavItems
            .filter(
              (item) =>
                (!isGroup(item) && (item as MenuItemDef).devOnly !== true) ||
                isGroup(item),
            )
            .map((item) => {
              if (isGroup(item)) {
                const isChildSelected = item.children.some(
                  (c) => location.pathname === c.path,
                )
                return (
                  <Box key={item.group}>
                    <ListItem disablePadding sx={{ display: 'block' }}>
                      <ListItemButton
                        onClick={() =>
                          setGroupOpen((o) => ({ ...o, [item.group]: !o[item.group] }))
                        }
                        selected={isChildSelected && !groupOpen[item.group]}
                        sx={{
                          minHeight: 48,
                          justifyContent: drawerOpen ? 'initial' : 'center',
                          px: 2.5,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: drawerOpen ? 1.5 : 'auto',
                            justifyContent: 'center',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.group}
                          sx={{ opacity: drawerOpen ? 1 : 0 }}
                        />
                        {drawerOpen &&
                          (groupOpen[item.group] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ChevronRightIcon />
                          ))}
                      </ListItemButton>
                    </ListItem>
                    <Collapse
                      in={groupOpen[item.group] && drawerOpen}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List disablePadding>
                        {item.children.map((child) => {
                          const isDisabled = !hasSites && child.path !== '/sites'
                          const isSelected = location.pathname === child.path
                          return (
                            <ListItem
                              key={child.text}
                              disablePadding
                              sx={{ display: 'block' }}
                            >
                              <ListItemButton
                                onClick={(e) => handleMenuItemClick(child.path, e)}
                                disabled={isDisabled}
                                selected={isSelected}
                                sx={{ minHeight: 40, pl: 4 }}
                              >
                                <ListItemText primary={child.text} />
                              </ListItemButton>
                            </ListItem>
                          )
                        })}
                      </List>
                    </Collapse>
                  </Box>
                )
              }

              const flatItem = item as MenuItemDef
              const isDisabled = !hasSites && flatItem.path !== '/sites'
              const isSelected = location.pathname === flatItem.path

              return (
                <ListItem key={flatItem.text} disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    onClick={(e) => handleMenuItemClick(flatItem.path, e)}
                    disabled={isDisabled}
                    selected={isSelected}
                    sx={{
                      minHeight: 48,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: drawerOpen ? 1.5 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {flatItem.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={flatItem.text}
                      sx={{ opacity: drawerOpen ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}
          <Box sx={{ flexGrow: 1 }} />
          {visibleNavItems
            .filter((item) => !isGroup(item) && (item as MenuItemDef).devOnly === true)
            .map((item) => {
              const flatItem = item as MenuItemDef
              const isSelected = location.pathname === flatItem.path
              return (
                <ListItem key={flatItem.text} disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    onClick={(e) => handleMenuItemClick(flatItem.path, e)}
                    selected={isSelected}
                    sx={{
                      minHeight: 48,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: drawerOpen ? 1.5 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {flatItem.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={flatItem.text}
                      sx={{ opacity: drawerOpen ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}
        </List>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          transition: (theme) =>
            theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  )
}
